import React, { useCallback, useEffect } from 'react';

import { useIntl } from 'react-intl';
import styled from 'styled-components';

import Modal, { ModalSize, modalSizeStyles } from 'components/modal';
import RestaurantRedemptionModal from 'components/offer-redemption-modal/redemption-modal';
import { logOfferClick } from 'components/offers/custom-events';
import { RedemptionButtons } from 'components/offers/ui-refresh/redemption-buttons';
import { IAttemptOfferRedemptionArgs } from 'components/offers/ui-refresh/types';
import { useOfferDetails } from 'components/offers/ui-refresh/use-offer-details';
import { useOfferRedemptionFlow } from 'components/offers/ui-refresh/use-offer-redemption-flow';
import Picture from 'components/picture';
import { LockedCard } from 'components/rewards/locked-offers-card/locked-card';
import { Container as CardContainer } from 'components/rewards/locked-offers-card/locked-offers-card.styled';
import { cardAttributes } from 'components/rewards/locked-offers/utils';
import SanityBlockRenderer from 'components/sanity-block-renderer';
import useDialogModal from 'hooks/use-dialog-modal';
import { useAuthContext } from 'state/auth';
import { useMParticleContext } from 'state/mParticle';
import { useOffersContext } from 'state/offers';
import { ILockedOffer } from 'state/offers/hooks/use-locked-offers';
import noop from 'utils/noop';

const StyledModal = styled(Modal)`
  display: flex;
  justify-content: center;

  ${Styles.desktop`
    min-width: ${modalSizeStyles.minWidth.REGULAR};
  `}
`;

const Container = styled.div`
  height: 100%;
  width: 100%;
  padding: calc(4rem + env(safe-area-inset-top)) 1rem 1rem;
  display: flex;
  gap: 1rem;
  flex-direction: column;
  align-items: center;
  background-color: ${Styles.color.white};
  overflow: auto;

  ${Styles.desktop`
    padding: calc(1.5rem + env(safe-area-inset-top)) 1rem;
  `}

  & > * {
    width: 100%;
    max-width: 320px;

    ${Styles.mobileTiny`
      max-width: 240px;
    `}

    ${Styles.desktop`
      max-width: 380px;
    `}
  }
`;

const StyledPicture = styled(Picture)`
  height: auto;
  min-height: 160px;

  ${Styles.mobileTiny`
    min-height: 120px;
  `}

  ${Styles.desktop`
    min-height: 190px;
  `}
`;

const TitleContainer = styled.div`
  text-align: center;
`;

const Heading = styled.h3`
  margin: 0;
  font-family: ${Styles.fontFamily.base};
  font-size: 1.5rem;
  line-height: 1;
  text-transform: uppercase;

  & small {
    font-size: 0.55em;
  }
`;

const Description = styled.p`
  margin: 0.15rem 0 0;
  font-family: ${Styles.fontFamily.body};
  line-height: 1.25;
  font-size: 0.8125rem;
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  // Removing Container gap as RedemptionButtons already have margin-top
  margin-top: -1rem;
`;

const CardWrapper = styled(CardContainer)`
  width: 100%;
  height: unset;
  min-width: unset;
  margin: 0;
  background-color: ${Styles.color.white};
`;

const InfoContainer = styled.div`
  display: flex;
  flex-direction: column;

  & > h4 {
    font-weight: bold;
    font-size: 1rem;
    margin: 0;
  }
`;

interface IRedemptionLockedOfferModalProps {
  offer: ILockedOffer;
  onDismiss: () => void;
}

const RedemptionLockedOfferDialog = ({ offer, onDismiss }: IRedemptionLockedOfferModalProps) => {
  const { formatMessage } = useIntl();
  const { isAuthenticated } = useAuthContext();
  const { clearSelectedOffer, lockedOffers = [], selectedOffer } = useOffersContext();
  const mParticle = useMParticleContext();

  const authenticated = isAuthenticated();
  const offersCount = lockedOffers.length;
  const offerIndex = lockedOffers.findIndex(o => o._id === offer._id);

  const {
    loginRequiredForRedemption,
    mobileRedemptionAvailable,
    disableInRestaurantOfferRedemptions,
    disableMobileOfferRedemptions,
    offerAvailableRightNow,
    redemptionErrorMessage,
    restaurantRedemptionAvailable,
    showRestaurantRedemptionButton,
    showMobileRedemptionButton,
    restaurantRedemptionButtonUiType,
    showSignUpToRedeem,
    noPossibleRedemption,
  } = useOfferDetails({
    authenticated,
    offer,
    offerIndex,
    offersCount,
  });

  const {
    attemptOfferRedemption: onRedeemOffer,
    onConfirmReadyToRedeem,
    onCancelReadyToRedeem,
    onDismissRestaurantRedemption,
    showReadyToRedeemDialog,
    showRestaurantRedemptionModal,
    restaurantRedemptionStartTime,
  } = useOfferRedemptionFlow({
    offer,
    authenticated,
    offerAvailableRightNow,
    mobileRedemptionAvailable,
    restaurantRedemptionAvailable,
    loginRequiredForRedemption,
  });

  const [ReadyToRedeemDialog, openReadyToRedeemDialog] = useDialogModal({
    modalAppearanceEventMessage: 'Confirm Ready to Redeem',
    onConfirm: () => onConfirmReadyToRedeem(true),
    onDismiss: () => onCancelReadyToRedeem(true),
    showCancel: false,
  });

  useEffect(() => {
    if (showReadyToRedeemDialog) {
      openReadyToRedeemDialog();
    }
  }, [openReadyToRedeemDialog, showReadyToRedeemDialog]);

  const onClickRedemption = useCallback(
    (redemptionMethod: IAttemptOfferRedemptionArgs) => {
      onRedeemOffer(redemptionMethod);
      logOfferClick(offer, offerIndex, offersCount, mParticle);
    },
    [mParticle, offer, offerIndex, offersCount, onRedeemOffer]
  );

  const onDismissRestaurantRedeemDialog = useCallback(() => {
    onDismissRestaurantRedemption();
    clearSelectedOffer({ doLog: false });
    onDismiss();
  }, [clearSelectedOffer, onDismiss, onDismissRestaurantRedemption]);

  const {
    mode,
    header,
    description,
    expiresText,
    terms,
    howToRedeemSanityContent,
  } = cardAttributes(offer, formatMessage);

  const isComplete = offer.stepsCompleted === offer.stepsTotal;

  return (
    <StyledModal
      size={ModalSize.SMALL}
      onDismiss={() => {
        clearSelectedOffer();
        onDismiss();
      }}
      mParticleEventData={{
        modalAppearanceEventMessage: 'Redeem Locked Offer',
      }}
    >
      <Container data-testid="redemption-locked-offer-dialog">
        <StyledPicture
          alt={formatMessage({ id: 'lockedOfferImage' })}
          image={offer?.localizedImage?.locale?.app!}
        />
        <TitleContainer>
          <Heading>
            <SanityBlockRenderer content={header} />
          </Heading>
          <Description>
            <SanityBlockRenderer content={description} />
          </Description>
        </TitleContainer>
        {isComplete ? (
          <ButtonWrapper data-testid="redemption-buttons">
            <RedemptionButtons
              disableMobileOfferRedemptions={disableMobileOfferRedemptions}
              disableInRestaurantOfferRedemptions={disableInRestaurantOfferRedemptions}
              showMobileRedemptionButton={showMobileRedemptionButton}
              showSignUpToRedeem={showSignUpToRedeem}
              showRestaurantRedemptionButton={showRestaurantRedemptionButton}
              onRedeemOffer={onClickRedemption}
              redemptionErrorMessage={redemptionErrorMessage}
              restaurantRedemptionButtonUiType={restaurantRedemptionButtonUiType}
              noPossibleRedemption={noPossibleRedemption}
            />
          </ButtonWrapper>
        ) : (
          <CardWrapper>
            <LockedCard
              header={null}
              subheader={description || null}
              expiresText={expiresText}
              stepsCompleted={offer.stepsCompleted}
              offerState={offer.offerState}
              progressData={offer.steps}
              mode={mode}
              showButton={false}
              showBadge={false}
              onClick={noop}
            />
          </CardWrapper>
        )}
        <InfoContainer>
          <h4>{formatMessage({ id: 'howToRedeem' })}</h4>
          <SanityBlockRenderer content={howToRedeemSanityContent} />
        </InfoContainer>
        <InfoContainer>
          <h4>{formatMessage({ id: 'offerTerms' })}</h4>
          <SanityBlockRenderer content={terms} />
        </InfoContainer>
      </Container>
      <ReadyToRedeemDialog
        data-testid="ready-to-redeem-dialog"
        heading={formatMessage({ id: 'couponWillExpire' })}
        body={formatMessage({ id: 'confirmDialogBody' })}
        buttonLabel={formatMessage({ id: 'redeem' })}
        showCloseButton
      />
      {showRestaurantRedemptionModal && selectedOffer && (
        <RestaurantRedemptionModal
          selectedOffer={selectedOffer}
          redemptionStartTime={restaurantRedemptionStartTime}
          onDismiss={onDismissRestaurantRedeemDialog}
        />
      )}
    </StyledModal>
  );
};

export default RedemptionLockedOfferDialog;
